import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const RAID_SEASON_ERROR_MESSAGES = {
  platform_season_managed: '공용 시즌 설정은 플랫폼 관리자만 변경할 수 있습니다.',
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_manager_required: '대표·부대표·소유자·관리자만 레이드 차수를 설정할 수 있습니다.',
  invalid_season_no: '레이드 차수는 1 이상 숫자로 입력해주세요.',
  raid_season_not_found: '수정할 레이드 차수를 찾지 못했습니다.',
  raid_season_not_active: '현재 활성 상태인 차수만 종료할 수 있습니다.',
  season_number_conflict: '이미 같은 차수가 등록되어 있습니다.',
  season_has_raid_records: '실전 기록이 있는 차수는 삭제할 수 없습니다.',
  season_has_practice_records: '모의전 기록이 있는 차수는 삭제할 수 없습니다.',
  season_has_sync_history: '동기화 이력이 있는 차수는 삭제할 수 없습니다.',
  invalid_boss_payload: '보스 설정 형식을 확인해주세요.',
  five_bosses_required: '레이드 보스는 1번부터 5번까지 모두 설정해야 합니다.',
  invalid_boss_name: '보스 이름을 확인해주세요.',
  invalid_boss_element: '보스 약점 속성을 확인해주세요.',
  boss_comment_too_long: '보스 설명은 300자 이하로 입력해주세요.',
  boss_image_url_too_long: '보스 이미지 주소가 너무 깁니다.',
  invalid_boss_coefficient: '보스 보정값은 0보다 큰 값으로 입력해주세요.',
  invalid_boss_hp: '1~3단 보스 체력은 0보다 큰 값으로 입력해주세요.',
};

export const RAID_ELEMENTS = ['작열', '수냉', '전격', '풍압', '철갑'];

const SMALL_BOSS_HP = {
  phase1_hp: '998.562792',
  phase2_hp: '1497.844188',
  phase3_hp: '2924.452958',
};

const LARGE_BOSS_HP = {
  phase1_hp: '1508.418136',
  phase2_hp: '2262.627204',
  phase3_hp: '3492.309015',
};

const DEFAULT_ELEMENTS = ['작열', '풍압', '수냉', '전격', '철갑'];

function requireSupabaseClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  }
  return supabase;
}

function createRaidSeasonError(error, fallbackMessage) {
  const rawMessage = String(error?.message || '');
  const matchedCode = Object.keys(RAID_SEASON_ERROR_MESSAGES).find((code) => rawMessage.includes(code));
  const nextError = new Error(
    RAID_SEASON_ERROR_MESSAGES[matchedCode]
      || fallbackMessage
      || '레이드 차수 정보를 처리하지 못했습니다.',
  );
  nextError.code = matchedCode || error?.code || 'unknown_error';
  return nextError;
}

async function requireAuthenticatedClient() {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.getSession();
  if (error || !data?.session?.access_token) {
    throw createRaidSeasonError({ message: 'authentication_required' });
  }
  return client;
}

function numberText(value, fallback = '') {
  return value === null || value === undefined || value === '' ? fallback : String(value);
}

export function createDefaultRaidBosses() {
  return Array.from({ length: 5 }, (_, index) => {
    const bossNo = index + 1;
    const hpPreset = [3, 5].includes(bossNo) ? LARGE_BOSS_HP : SMALL_BOSS_HP;
    return {
      boss_no: bossNo,
      boss_name: `${bossNo}보스`,
      element: DEFAULT_ELEMENTS[index],
      boss_comment: '',
      image_url: '',
      coefficient: '1',
      ...hpPreset,
      phase4_infinite: bossNo === 5,
    };
  });
}

function normalizeBosses(rawBosses) {
  const rows = Array.isArray(rawBosses) ? rawBosses : [];
  const byBossNo = new Map(rows.map((boss) => [Number(boss?.boss_no), boss]));

  return createDefaultRaidBosses().map((fallback) => {
    const raw = byBossNo.get(fallback.boss_no);
    if (!raw) return fallback;
    return {
      boss_no: fallback.boss_no,
      boss_name: String(raw.boss_name || fallback.boss_name),
      element: RAID_ELEMENTS.includes(raw.element) ? raw.element : fallback.element,
      boss_comment: String(raw.boss_comment || ''),
      image_url: String(raw.image_url || ''),
      coefficient: numberText(raw.coefficient, '1'),
      phase1_hp: numberText(raw.phase1_hp, fallback.phase1_hp),
      phase2_hp: numberText(raw.phase2_hp, fallback.phase2_hp),
      phase3_hp: numberText(raw.phase3_hp, fallback.phase3_hp),
      phase4_infinite: Boolean(raw.phase4_infinite),
    };
  });
}

function normalizeSeason(rawSeason) {
  if (!rawSeason) return null;
  return {
    season_no: Number(rawSeason.season_no),
    status: String(rawSeason.status || 'draft'),
    bosses: normalizeBosses(rawSeason.bosses),
  };
}

export function normalizeRaidSetup(rawSetup) {
  const setup = rawSetup && typeof rawSetup === 'object' ? rawSetup : {};
  const activeSeasonNo = Number(setup.active_season_no);
  return {
    unionId: String(setup.union_id || ''),
    unionName: String(setup.union_name || ''),
    viewerRole: String(setup.viewer_role || ''),
    canManage: false,
    activeSeasonNo: Number.isInteger(activeSeasonNo) && activeSeasonNo > 0 ? activeSeasonNo : null,
    seasons: (Array.isArray(setup.seasons) ? setup.seasons : [])
      .map((season) => ({
        season_no: Number(season.season_no),
        status: String(season.status || 'draft'),
      }))
      .filter((season) => Number.isInteger(season.season_no) && season.season_no > 0),
    selectedSeason: normalizeSeason(setup.selected_season),
  };
}

function normalizeBossPayload(bosses) {
  return normalizeBosses(bosses).map((boss) => ({
    boss_no: boss.boss_no,
    boss_name: String(boss.boss_name || '').trim(),
    element: boss.element,
    boss_comment: String(boss.boss_comment || '').trim() || null,
    image_url: String(boss.image_url || '').trim() || null,
    coefficient: Number(boss.coefficient),
    phase1_hp: Number(boss.phase1_hp),
    phase2_hp: Number(boss.phase2_hp),
    phase3_hp: Number(boss.phase3_hp),
  }));
}

function normalizeSeasonNo(seasonNo) {
  const normalized = Number(seasonNo);
  if (!Number.isInteger(normalized) || normalized < 1 || normalized > 9999) {
    throw createRaidSeasonError({ message: 'invalid_season_no' });
  }
  return normalized;
}

export async function loadRaidSeasonSetup(seasonNo = null) {
  const client = await requireAuthenticatedClient();
  const requested = seasonNo === null || seasonNo === undefined || seasonNo === '' ? null : Number(seasonNo);
  const { data, error } = await client.rpc('get_union_raid_setup', {
    requested_season_no: requested,
  });

  if (error) {
    throw createRaidSeasonError(
      error,
      '레이드 차수 정보를 불러오지 못했습니다. Supabase 실행 대기 SQL을 확인해주세요.',
    );
  }
  return normalizeRaidSetup(data);
}

export async function saveRaidSeasonSetup({ season, activate }) {
  const seasonNo = normalizeSeasonNo(season?.season_no);
  const sourceSeasonNo = season?.original_season_no === null || season?.original_season_no === undefined
    ? null
    : normalizeSeasonNo(season.original_season_no);

  const client = await requireAuthenticatedClient();
  const { data, error } = await client.rpc('save_union_raid_setup', {
    target_season_no: seasonNo,
    source_season_no: sourceSeasonNo,
    activate_now: Boolean(activate),
    bosses_payload: normalizeBossPayload(season?.bosses),
  });

  if (error) {
    throw createRaidSeasonError(
      error,
      activate ? '레이드 차수 활성화에 실패했습니다.' : '레이드 차수 저장에 실패했습니다.',
    );
  }
  return normalizeRaidSetup(data);
}

export async function closeRaidSeasonSetup(seasonNo) {
  const normalized = normalizeSeasonNo(seasonNo);
  const client = await requireAuthenticatedClient();
  const { data, error } = await client.rpc('close_union_raid_season', {
    target_season_no: normalized,
  });

  if (error) {
    throw createRaidSeasonError(error, '레이드 차수를 종료하지 못했습니다.');
  }
  return normalizeRaidSetup(data);
}

export async function deleteRaidSeasonSetup(seasonNo) {
  const normalized = normalizeSeasonNo(seasonNo);
  const client = await requireAuthenticatedClient();
  const { data, error } = await client.rpc('delete_union_raid_season', {
    target_season_no: normalized,
  });

  if (error) {
    throw createRaidSeasonError(error, '레이드 차수를 삭제하지 못했습니다.');
  }
  return normalizeRaidSetup(data);
}
